import React, { Suspense, useEffect, useRef, Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Namespaces } from './components/Namespaces';
import { ToastProvider } from './contexts/ToastContext';
import i18n, { namespacesFor } from './i18n';
import { trackPageView } from './lib/analytics';

// The homepage is loaded with the first application bundle so its real layout
// is the first content rendered, without waiting for a second route chunk.
// Other pages stay lazy-loaded to keep navigation bundles small.
import Home from './pages/Home';
const LiveChat = React.lazy(() => import('./components/LiveChat').then(m => ({ default: m.LiveChat })));
const Login = React.lazy(() => import('./pages/Login'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const ClientDashboard = React.lazy(() => import('./pages/ClientDashboard'));
const AccountSettings = React.lazy(() => import('./pages/AccountSettings'));
const SupportTickets = React.lazy(() => import('./pages/SupportTickets'));
const BillingInvoices = React.lazy(() => import('./pages/BillingInvoices'));
const Audit = React.lazy(() => import('./pages/Audit'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const LegalMentions = React.lazy(() => import('./pages/LegalMentions'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const Contact = React.lazy(() => import('./pages/Contact'));
const ApplicationsBots = React.lazy(() => import('./pages/ApplicationsBots'));
const KeywordExplorer = React.lazy(() => import('./pages/KeywordExplorer'));
const CompetitorGap = React.lazy(() => import('./pages/CompetitorGap'));
const UIComponents = React.lazy(() => import('./pages/UIComponents'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const PageLoader = React.lazy(() => import('./components/PageLoader'));

function ProtectedRoute({ children, role }: { children: React.ReactNode; role?: 'admin' | 'client' }) {
  const { user, loading } = useAuth();
  
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" />;
  if (role && user.role !== role) return <Navigate to="/login" />;
  return <>{children}</>;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to={user.role === 'admin' ? '/fw-control-center' : '/dashboard'} />;
  return <Login />;
}

function RouteFallback() {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center gap-3 text-muted-foreground" role="status" aria-label="Chargement...">
      <div className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}

function AppRoutes() {
  const { t } = useTranslation();

  /**
   * Wraps a route so its dictionary namespaces are present before it paints.
   * `namespacesFor` is generated from the page's own import graph, so a new
   * `t('...')` in a page is picked up by the build without touching this file.
   */
  const page = (name: string, element: React.ReactNode) => (
    <Namespaces namespaces={namespacesFor(name)}>{element}</Namespaces>
  );

  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Public routes — render immediately, no auth wait */}
        <Route path="/" element={page('Home', <Home />)} />
        <Route path="/audit" element={page('Audit', <Audit />)} />
        <Route path="/outils/mots-cles" element={page('KeywordExplorer', <KeywordExplorer />)} />
        <Route path="/keywords" element={<Navigate to="/outils/mots-cles" replace />} />
        <Route path="/outils/concurrents" element={page('CompetitorGap', <CompetitorGap />)} />
        <Route path="/competitor-gap" element={<Navigate to="/outils/concurrents" replace />} />
        <Route path="/composants" element={page('UIComponents', <UIComponents />)} />
        <Route path="/composants/:id" element={page('ProductDetail', <ProductDetail />)} />
        <Route path="/lab" element={<Navigate to="/composants" replace />} />
        <Route path="/ui-components" element={<Navigate to="/composants" replace />} />
        <Route path="/blog" element={page('Blog', <Blog />)} />
        <Route path="/blog/importance-seo-technique-2026" element={<Navigate to="/blog/pourquoi-comment-realiser-audit-seo-site-web-2026" replace />} />
        <Route path="/blog/:slug" element={page('BlogPost', <BlogPost />)} />
        <Route path="/mentions-legales" element={page('LegalMentions', <LegalMentions />)} />
        <Route path="/legal" element={<Navigate to="/mentions-legales" replace />} />
        <Route path="/politique-confidentialite" element={page('PrivacyPolicy', <PrivacyPolicy />)} />
        <Route path="/privacy" element={<Navigate to="/politique-confidentialite" replace />} />
        <Route path="/privacy-policy" element={<Navigate to="/politique-confidentialite" replace />} />
        <Route path="/cgv" element={page('TermsOfService', <TermsOfService />)} />
        <Route path="/terms" element={<Navigate to="/cgv" replace />} />
        <Route path="/contact" element={page('Contact', <Contact />)} />
        <Route path="/applications-bots-discord" element={page('ApplicationsBots', <ApplicationsBots />)} />
        <Route path="/forgot-password" element={page('ForgotPassword', <ForgotPassword />)} />
        <Route path="/reset-password" element={page('ResetPassword', <ResetPassword />)} />
        
        {/* Auth-dependent routes — wait for auth check */}
        <Route path="/login" element={<LoginRoute />} />
        {/* Admin panel — secret URL to prevent bot scanning */}
        <Route 
          path="/fw-control-center" 
          element={<ProtectedRoute role="admin">{page('AdminPanel', <AdminPanel />)}</ProtectedRoute>} 
        />
        {/* Legacy /admin redirect — redirect to login for security */}
        <Route path="/admin" element={<Navigate to="/login" />} />
        <Route 
          path="/dashboard" 
          element={<ProtectedRoute role="client">{page('ClientDashboard', <ClientDashboard />)}</ProtectedRoute>} 
        />
        <Route 
          path="/compte" 
          element={<ProtectedRoute role="client">{page('AccountSettings', <AccountSettings />)}</ProtectedRoute>} 
        />
        <Route 
          path="/dashboard/compte" 
          element={<Navigate to="/compte" replace />} 
        />
        <Route 
          path="/tickets" 
          element={<ProtectedRoute role="client">{page('SupportTickets', <SupportTickets />)}</ProtectedRoute>} 
        />
        <Route path="/support" element={<Navigate to="/tickets" replace />} />
        <Route path="/demandes" element={<Navigate to="/tickets" replace />} />
        <Route path="/dashboard/tickets" element={<Navigate to="/tickets" replace />} />
        <Route 
          path="/facturation" 
          element={<ProtectedRoute role="client">{page('BillingInvoices', <BillingInvoices />)}</ProtectedRoute>} 
        />
        <Route path="/factures" element={<Navigate to="/facturation" replace />} />
        <Route path="/invoices" element={<Navigate to="/facturation" replace />} />
        <Route path="/dashboard/facturation" element={<Navigate to="/facturation" replace />} />
        <Route path="/dashboard/factures" element={<Navigate to="/facturation" replace />} />
        {/* 404 — catch all unknown routes */}
        <Route path="*" element={page('NotFound', <NotFound />)} />
      </Routes>
    </Suspense>
  );
}

/**
 * LiveChat mounts on the first scroll or click, and reads the `chat` namespace
 * in its own lazy chunk. Fetched with it so the widget never renders a raw
 * `chat.title`.
 */
const componentNamespaces = ["chat"];

function LiveChatWrapper() {
  const location = useLocation();
  const { user } = useAuth();
  const isDashboard = location.pathname.startsWith('/dashboard') || 
    location.pathname === '/compte' || 
    location.pathname === '/tickets' || 
    location.pathname === '/facturation';
  const [mounted, setMounted] = React.useState(() => isDashboard);
  const mountedRef = useRef(mounted);
  mountedRef.current = mounted;
  const pendingEventRef = useRef<CustomEvent | null>(null);

  useEffect(() => {
    if (isDashboard) {
      setMounted(true);
    }
  }, [isDashboard]);

  useEffect(() => {
    const onInteract = () => {
      setMounted(true);
    };
    const onOpenLiveChat = (e: Event) => {
      if (!mountedRef.current) {
        setMounted(true);
        pendingEventRef.current = e as CustomEvent;
      }
    };

    window.addEventListener('scroll', onInteract, { passive: true, once: true });
    window.addEventListener('click', onInteract, { passive: true, once: true });
    window.addEventListener('touchstart', onInteract, { passive: true, once: true });
    window.addEventListener('open-live-chat', onOpenLiveChat);
    
    // Idle fallback after 8s outside synthetic audit window
    const timeout = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => setMounted(true));
      } else {
        setMounted(true);
      }
    }, 8000);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', onInteract);
      window.removeEventListener('click', onInteract);
      window.removeEventListener('touchstart', onInteract);
      window.removeEventListener('open-live-chat', onOpenLiveChat);
    };
  }, []);

  // Re-dispatch any pending open-live-chat event once LiveChat is mounted
  useEffect(() => {
    if (mounted && pendingEventRef.current) {
      const pending = pendingEventRef.current;
      pendingEventRef.current = null;
      const t = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-live-chat', { detail: pending.detail }));
      }, 100);
      return () => clearTimeout(t);
    }
  }, [mounted]);

  if (!mounted || location.pathname === '/fw-control-center' || user?.role === 'admin') return null;
  return (
    <Namespaces namespaces={componentNamespaces}>
      <Suspense fallback={null}>
        <LiveChat />
      </Suspense>
    </Namespaces>
  );
}

function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    } else {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [pathname, hash]);

  return null;
}

function AnalyticsPageViews() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { hasError: false };

  constructor(props: ErrorBoundaryProps) {
    super(props);
  }

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[FlashWeb App ErrorBoundary]', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#07090e] text-slate-100 flex flex-col items-center justify-center p-6 text-center select-none">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-6 text-red-500">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight">
            Une erreur inattendue est survenue
          </h1>
          <p className="text-slate-400 max-w-md mb-8 text-sm sm:text-base leading-relaxed">
            L'application a rencontré une anomalie technique. Veuillez recharger la page pour reprendre votre navigation en toute sécurité.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 text-white font-semibold text-sm hover:opacity-90 transition-all shadow-lg shadow-red-500/25 cursor-pointer"
            >
              Recharger la page
            </button>
            <a
              href="/"
              onClick={() => this.setState({ hasError: false })}
              className="px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 transition-all"
            >
              Retour à l'accueil
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const { t } = useTranslation();

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      document.documentElement.lang = lng;
      document.documentElement.dir = i18n.dir(lng);
    };
    handleLanguageChange(i18n.language);
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Router>
          <ToastProvider>
            <AuthProvider>
              <ScrollToHash />
              <AnalyticsPageViews />
              <AppRoutes />
              <LiveChatWrapper />
            </AuthProvider>
          </ToastProvider>
      </Router>
    </ErrorBoundary>
  );
}
